import { Button, Stack, Typography } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { IconButton, InformationModal } from 'utoniq-ui'

import {
  ArtistThreadCategory,
  ThreadCreateQuery,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { BrowserLink } from '@/components/lib/BrowserLink'
import { FullscreenModal } from '@/components/organisms/functionals'
import {
  handleError,
  useForm,
  useMessage,
  useRouter,
  useTranslation,
} from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { getHelpUrl } from '@/lib/helpUrl'

import {
  CommunityNoteModal,
  CommunityNoteModalProps,
} from './CommunityNoteModal'
import {
  defaultValues,
  schema,
  ThreadCreateForm,
  ThreadFormValues,
} from './ThreadCreateForm'
import { ThreadCreatePreview } from './ThreadCreatePreview'

export * from './fragments'

export type State = {
  artistId: string
  artist: ThreadCreateQuery['artist']
  user: ThreadCreateQuery['user']
  openCommunityNote: boolean
}

type ActionLinkCellProps = {
  disabled: boolean
  onConfirmSaveDraft: () => void
  onShowPreview: () => void
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onCreateThread: (inputs: ThreadFormValues) => Promise<void>
  onSaveDraft: (inputs: ThreadFormValues) => Promise<void>
  onAcceptCommunityNote: CommunityNoteModalProps['onSubmit']
}

export const ThreadCreateTemplate: Template<State, Action> = ({
  state,
  onCreateThread,
  // onSaveDraft,
  onAcceptCommunityNote,
}) => {
  const { t } = useTranslation('threadCreate')
  const { router, generatePath, routerPages, routerNames } = useRouter()
  const defaultBackPage = generatePath(routerPages.ArtistThreads, {
    artistId: state.artist?.id ?? '#',
  })
  const { showMessage, showError } = useMessage()
  const [open, setOpen] = useState<boolean>(false)
  const [artistDraftSaving, setArtistDraftSaving] = useState<boolean>(false)
  const [artistCreating, setArtistCreating] = useState<boolean>(false)
  const [openPreview, setOpenPreview] = useState<boolean>(false)
  const {
    handleSubmit,
    register,
    control,
    setValue,
    getValues,
    setError,
    formState: { errors, isValid },
  } = useForm<ThreadFormValues>({
    schema,
    defaultValues,
  })
  const categoryOptions = useMemo(
    () =>
      categories.map(category => ({
        value: category,
        label: t([`category_${category}`, category]),
      })),
    [t]
  )

  const onSubmit = handleSubmit(async values => {
    setArtistCreating(true)
    try {
      await onCreateThread(values)
      await router.goBack(defaultBackPage)
      showMessage(t('postComplete'), 'success')
    } catch (error) {
      handleError<ThreadFormValues>(error as Error, setError, showError)
    }
    setArtistCreating(false)
  })

  const handleSaveDraft = handleSubmit(async () => {
    setArtistDraftSaving(true)
    try {
      /**
       * @description
       * 下書き保存機能は別タスクにて実装
       * https://app.clickup.com/t/908127/UT-5151
       *
       * await onSaveDraft(values)
       * setOpen(false)
       */
      router.goBack(
        generatePath(routerPages.ArtistThreads, {
          artistId: state.artist?.id ?? '#',
        })
      )
    } catch (error) {
      showError(error as Error)
    }
    setArtistDraftSaving(false)
  })

  const handleShowPreview = useCallback(() => {
    setOpenPreview(true)
  }, [])

  const handleCloseWithoutSave = useCallback(() => {
    setOpen(false)
    router.goBack(defaultBackPage)
  }, [router, state.artist?.id])

  return (
    <>
      <Head title={routerNames.ArtistThreadCreate} />

      <BaseLayout
        navigation={{
          type: 'threadForm',
          props: {
            disabled: !isValid,
            onConfirmSaveDraft: handleSaveDraft,
            onShowPreview: handleShowPreview,
          },
        }}
        header={{
          left: (
            <IconButton.Close
              edge="start"
              onClick={() => {
                /**
                 * @description
                 * 下書き保存機能は別タスクにて実装
                 * https://app.clickup.com/t/908127/UT-5151
                 *
                 * if (isDirty) {
                 *  setOpen(true)
                 *  return
                 * }
                 */

                return router.goBack(defaultBackPage)
              }}
            />
          ),
          title: t('title'),
          right: (
            <Button
              type="submit"
              size="small"
              disabled={!isValid}
              loading={artistCreating}
              onClick={onSubmit}
              sx={{ mr: -1 }}
            >
              {t('public')}
            </Button>
          ),
        }}
      >
        <ThreadCreateForm
          hasPatron={!!state.artist?.patron?.id}
          control={control}
          errors={errors}
          onSubmit={onSubmit}
          register={register}
          setValue={setValue}
          categoryOptions={categoryOptions}
          userId={state.user?.id ?? ''}
        />
        <NoticeTextCell />
      </BaseLayout>
      <CommunityNoteModal
        open={state.openCommunityNote}
        onSubmit={onAcceptCommunityNote}
      />
      <InformationModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('draftTitle')}
        content={t('draftDesc')}
        buttons={
          <>
            <Button
              sx={{ minWidth: 212 }}
              variant="contained"
              color="primary"
              size="large"
              loading={artistDraftSaving}
              onClick={handleSaveDraft}
            >
              {t('saveToDraft')}
            </Button>
            <Button
              sx={{ minWidth: 212 }}
              variant="outlined"
              color="primary"
              size="large"
              disabled={artistDraftSaving}
              onClick={handleCloseWithoutSave}
            >
              {t('closeWithoutSave')}
            </Button>
          </>
        }
      />

      <FullscreenModal open={openPreview} onClose={() => setOpenPreview(false)}>
        <ThreadCreatePreview
          onClose={() => setOpenPreview(false)}
          thread={getValues()}
          categoryOptions={categoryOptions}
          user={state.user ?? null}
        />
      </FullscreenModal>
    </>
  )
}

export const ActionLinkCell: Cell<ActionLinkCellProps> = ({
  onConfirmSaveDraft,
  onShowPreview,
  disabled,
}) => {
  const { t } = useTranslation('threadCreate')
  const actions = [
    {
      title: t('saveDraft'),
      onClick: onConfirmSaveDraft,
    },
    {
      title: t('preview'),
      onClick: onShowPreview,
    },
  ]
  return (
    <CellFrame sx={{ bgcolor: 'background.default' }}>
      <Stack direction="row" spacing={3} sx={{ p: 3 }}>
        {actions.map((action, index) => {
          /**
           * @description
           * 下書き保存機能は別タスクにて実装
           * https://app.clickup.com/t/908127/UT-5151
           */
          if (action.title === t('saveDraft')) return

          return (
            <Button
              key={index}
              variant="text"
              onClick={action.onClick}
              disabled={disabled}
              disableElevation
              sx={{
                bgcolor: 'transparent',
                color: 'text.primary',
                '&.Mui-disabled': {
                  bgcolor: 'transparent',
                  color: 'text.disabled',
                },
              }}
            >
              {action.title}
            </Button>
          )
        })}
      </Stack>
    </CellFrame>
  )
}

const categories = [
  ArtistThreadCategory.TradeCall,
  ArtistThreadCategory.Event,
  ArtistThreadCategory.Request,
  ArtistThreadCategory.Faq,
]

const NoticeTextCell = () => {
  const { t } = useTranslation()
  return (
    <CellFrame sx={{ pt: 1, px: 5, pb: 5 }}>
      <Stack direction="row">
        <Typography variant="caption" color="text.secondary">
          <BrowserLink variant="caption" href={getHelpUrl('thread')}>
            {t('noticePrefix')}
          </BrowserLink>
          {t('noticeSuffix')}
        </Typography>
      </Stack>
    </CellFrame>
  )
}
